import React from "react";
import { useNavigate } from "react-router-dom";
import { StatusBar } from "../components/Shell";
import Ico from "../components/Ico";
import { useAppStore } from "../components/AppStore";
import { rankTiers, currentUser } from "../mock";
import { ChevronLeft, Lock, CheckCircle2, Gift, Sparkles, PlusCircle, RotateCcw } from "lucide-react";

export default function Ranks() {
  const navigate = useNavigate();
  const { jobsDone, earnedTotal, rankIndex, completeDemoJob, resetRankProgress } = useAppStore();
  const current = rankTiers[rankIndex];
  const next = rankTiers[rankIndex + 1];

  const jobPct = next ? Math.min(100, Math.round((jobsDone / next.jobs) * 100)) : 100;
  const dollarPct = next ? Math.min(100, Math.round((earnedTotal / next.dollars) * 100)) : 100;

  return (
    <div className="gd-screen bg-[#eef1fb]">
      <div className="flex-shrink-0 px-5 pb-6" style={{ background: "linear-gradient(160deg,#3b57c9,#2b3f96)", borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
        <StatusBar dark={false} />
        <div className="mt-1 flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="gd-tap grid h-10 w-10 place-items-center rounded-full bg-white/15"><ChevronLeft size={20} color="#fff" /></button>
          <h1 className="text-2xl font-extrabold text-white">Rank &amp; Rewards</h1>
        </div>

        {/* current rank */}
        <div className="mt-4 flex items-center gap-4 rounded-2xl bg-white/15 p-4">
          <div className="grid h-16 w-16 place-items-center rounded-2xl" style={{ background: current.color }}>
            <Ico name={current.icon} size={30} color="#fff" />
          </div>
          <div className="flex-1 text-white">
            <p className="text-[11px] font-semibold text-white/75">Your current rank</p>
            <p className="text-lg font-black leading-tight">{current.title}</p>
            <p className="text-xs font-bold text-white/80">{current.tier} · {jobsDone} jobs · ${earnedTotal}</p>
          </div>
        </div>

        {/* progress to next */}
        {next ? (
          <div className="mt-3 rounded-2xl bg-white/15 p-3 text-white">
            <p className="text-xs font-bold">Next: {next.title} <span className="text-white/70">({next.jobs} jobs or ${next.dollars})</span></p>
            <div className="mt-2 space-y-2">
              <Bar label={`${jobsDone}/${next.jobs} jobs`} pct={jobPct} />
              <Bar label={`$${earnedTotal}/$${next.dollars}`} pct={dollarPct} />
            </div>
          </div>
        ) : (
          <div className="mt-3 rounded-2xl bg-white/15 p-3 text-center text-sm font-extrabold text-white">🏆 Top rank reached — Neighborhood CEO!</div>
        )}
      </div>

      <div className="gd-scroll px-4 pb-28 gd-stagger">
        {/* interactive demo controls */}
        <div className="mt-4 flex gap-2">
          <button onClick={() => completeDemoJob(25)} className="gd-orange-btn flex flex-1 items-center justify-center gap-2 rounded-2xl py-3 text-sm font-extrabold">
            <PlusCircle size={17} /> Complete a demo job (+$25)
          </button>
          <button onClick={resetRankProgress} className="gd-tap grid w-12 place-items-center rounded-2xl bg-white text-[#7b86ab] shadow"><RotateCcw size={18} /></button>
        </div>
        <p className="mt-1.5 text-center text-[11px] font-semibold text-[#9aa3c0]">Tap to simulate finishing jobs and watch {currentUser.name} rank up.</p>

        {/* ladder */}
        <h3 className="mb-2 mt-5 text-base font-extrabold text-[#1f2a52]">The Climb — Perks at Every Level</h3>
        <div className="space-y-3">
          {rankTiers.map((t, i) => {
            const unlocked = i <= rankIndex;
            const isCurrent = i === rankIndex;
            return (
              <div key={t.key} className="gd-card overflow-hidden p-4" style={{ outline: isCurrent ? `2px solid ${t.color}` : "none" }}>
                <div className="flex items-center gap-3">
                  <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-2xl" style={{ background: unlocked ? t.color : "#eef1fb" }}>
                    {unlocked ? <Ico name={t.icon} size={22} color="#fff" /> : <Lock size={20} color="#b6bdd6" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-black text-[#1f2a52]">{t.title}</p>
                      {isCurrent && <span className="rounded-full px-2 py-0.5 text-[9px] font-bold text-white" style={{ background: t.color }}>YOU</span>}
                    </div>
                    <p className="text-[11px] font-semibold text-[#9aa3c0]">{t.tier} · {t.jobs === 0 ? "Starting rank" : `${t.jobs} jobs or $${t.dollars}`}</p>
                  </div>
                  {unlocked ? <CheckCircle2 size={20} className="text-[#4fae3f]" /> : null}
                </div>
                <div className="mt-3 rounded-2xl p-3" style={{ background: unlocked ? "#f4f9f3" : "#f7f8fd" }}>
                  <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-extrabold" style={{ color: unlocked ? "#3d8f34" : "#9aa3c0" }}>
                    <Gift size={13} /> Perks &amp; Rewards
                  </div>
                  <div className="space-y-1">
                    {t.perks.map((p) => (
                      <div key={p} className="flex items-center gap-2 text-[12px] font-medium" style={{ color: unlocked ? "#3a4468" : "#a3abc9" }}>
                        <Sparkles size={12} className="flex-shrink-0" style={{ color: unlocked ? t.color : "#c7cde6" }} /> {p}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Bar({ label, pct }) {
  return (
    <div>
      <div className="mb-1 flex justify-between text-[10px] font-bold text-white/85"><span>{label}</span><span>{pct}%</span></div>
      <div className="h-2 rounded-full bg-white/25"><div className="h-2 rounded-full bg-white" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}
